import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Line } from 'react-chartjs-2';
import { Row, Col, Alert, ToggleButtonGroup } from 'react-bootstrap';
import dateFormat from "dateformat";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FaThermometerHalf, FaTint, FaFlask, FaChartLine, FaClock } from 'react-icons/fa';
import { BsCpu } from 'react-icons/bs';
import zoomPlugin from 'chartjs-plugin-zoom';
import DashMedidasSectionCard from './DashMedidasSectionCard/DashMedidasSectionCard';
import DashMedidasButton, { DashMedidasToggleButton } from './DashMedidasButton/DashMedidasButton';
import { GrainAnalysisCardsGrid } from './GrainIntelligentAnalysis/GrainIntelligentAnalysis';
import './GrainIntelligentAnalysis/GrainIntelligentAnalysis.css';
import { analyzeGrain } from '../../../hooks/grainAnalyzer';
import {
  getMeasureOperationId,
  hasMeasureMetadata,
} from '../../../utils/measureFields';
import MeasureMetadataModal from './MeasureMetadataModal';
import MeasurePointActionModal from './MeasurePointActionModal';
import {
  buildChartPointStyleProps,
  createMetadataTooltipCallbacks,
} from '../../../utils/chartMeasureMetadata';

import { motion, AnimatePresence } from "framer-motion";
import Chart from 'chart.js/auto';
import {
  chartDragSelectBridge,
  chartDragSelectPlugin,
  getChartIndicesInPixelRange,
  DRAG_SELECT_THRESHOLD,
} from './chartDragSelectPlugin';

Chart.register(zoomPlugin, chartDragSelectPlugin);

/** Altura dos gráficos (Chart.js) — desktop: hidden-mobile | mobile: block-mobile */
const CHART_HEIGHT_DESKTOP = 300;
const CHART_HEIGHT_MOBILE = 400;

const MOVING_AVERAGE_ACTIVE = {
  simple: false,
  exponential: false,
  weighted: false,
};
const MOVING_AVERAGE_PERIOD = 5;
const EXPONENTIAL_FILTER_ACTIVE = false;
const Z_SCORE_FILTER_ACTIVE = false;

const CHART_VIEW_MODE_STORAGE_KEY = "dashMedidasChartViewMode";

const CHART_VIEW_MODES = [
  { id: "separate", label: "Separados" },
  { id: "humidity-temperature", label: "Umid. + Temp." },
  { id: "humidity-temperature-density", label: "Umid. + Temp. + Dens.", requiresPh: true },
];

const getInitialChartViewMode = () => {
  try {
    const stored = localStorage.getItem(CHART_VIEW_MODE_STORAGE_KEY);
    return CHART_VIEW_MODES.some((mode) => mode.id === stored) ? stored : "separate";
  } catch {
    return "separate";
  }
};

const getMetricTooltipSuffix = (datasetLabel, sourceData = []) => {
  if (datasetLabel === "UMIDADE" || datasetLabel === "TOLERÂNCIA") return "%";
  if (datasetLabel === "TEMPERATURA") return "°C";
  if (datasetLabel === "DENSIDADE") {
    const unidadePh = sourceData?.[sourceData.length - 1]?.unidadePh || "";
    return unidadePh.includes("Kg/m³") ? " Kg/m³" : "";
  }
  return "";
};

const getDensityDataLabelSuffix = (sourceData = []) => {
  const unidadePh = sourceData?.[sourceData.length - 1]?.unidadePh || "";
  return unidadePh.includes("Kg/m³") ? " Kg/m³" : "";
};

const buildHumidityDataset = (
  humidityData,
  yAxisID,
  pointStyleProps = null,
  withPointStyles = false
) => ({
  label: "UMIDADE",
  data: humidityData,
  yAxisID,
  borderColor: "#1c4220",
  backgroundColor: "rgba(28, 66, 32, 0.14)",
  borderWidth: 2,
  fill: true,
  tension: 0.3,
  datalabels: buildCombinedDataLabels("%", "bottom"),
  ...(withPointStyles && pointStyleProps ? pointStyleProps : { pointRadius: 2 }),
});

const buildTemperatureDataset = (
  temperatureData,
  yAxisID,
  labelAlign = "top",
  pointStyleProps = null
) => ({
  label: "TEMPERATURA",
  data: temperatureData,
  yAxisID,
  borderColor: "#E52207",
  backgroundColor: "rgba(229, 34, 7, 0.08)",
  borderWidth: 2,
  fill: true,
  tension: 0.3,
  datalabels: buildCombinedDataLabels("°C", labelAlign),
  ...(pointStyleProps || { pointRadius: 2 }),
});

const buildPhDataset = (
  phData,
  phLabel,
  yAxisID,
  labelAlign = "top",
  valueSuffix = "",
  pointStyleProps = null
) => ({
  label: phLabel,
  data: phData,
  yAxisID,
  borderColor: "#1B5D43",
  backgroundColor: "rgba(27, 93, 67, 0.1)",
  borderWidth: 2,
  fill: true,
  tension: 0.3,
  datalabels: buildCombinedDataLabels(valueSuffix, labelAlign, "#1B5D43"),
  ...(pointStyleProps || { pointRadius: 2 }),
});

const buildSeparateDataLabels = (suffix = "", align = "end", color = "#4a5c52") => ({
  labels: {
    title: {
      color: "grey",
    },
  },
  display: "auto",
  align,
  color,
  font: {
    weight: 600,
    size: 11,
  },
  formatter: (value) => {
    if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
      return "";
    }
    return `${value}${suffix}`;
  },
});

const buildCombinedDataLabels = (suffix = "", align = "top", color = "#4a5c52") => ({
  display: "auto",
  align,
  anchor: "end",
  color,
  font: {
    weight: 600,
    size: 11,
  },
  formatter: (value) => {
    if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
      return "";
    }
    return `${value}${suffix}`;
  },
});

const calcScaleBounds = (values, fallbackMax = 100) => {
  const nums = values
    .filter((val) => val !== null && val !== undefined && !isNaN(val))
    .map((val) => parseFloat(val));

  if (!nums.length) {
    return { min: 0, max: fallbackMax };
  }

  let min = Math.min(...nums);
  let max = Math.max(...nums);
  min -= (min * 30) / 100;
  max += (max * 30) / 100;

  return { min, max };
};

const buildChartTooltipOptions = (callbacks = {}) => ({
  enabled: true,
  backgroundColor: "rgba(28, 66, 32, 0.72)",
  titleColor: "rgba(255, 255, 255, 0.96)",
  bodyColor: "rgba(255, 255, 255, 0.9)",
  footerColor: "rgba(255, 255, 255, 0.78)",
  borderColor: "rgba(143, 173, 21, 0.42)",
  borderWidth: 1,
  cornerRadius: 10,
  padding: { top: 10, right: 13, bottom: 10, left: 13 },
  boxPadding: 6,
  caretSize: 6,
  caretPadding: 9,
  titleFont: {
    size: 11,
    weight: "600",
    family: "'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  bodyFont: {
    size: 12,
    weight: "500",
    family: "'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  footerFont: {
    size: 11,
    weight: "400",
    family: "'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  titleSpacing: 6,
  bodySpacing: 5,
  displayColors: true,
  usePointStyle: true,
  boxWidth: 7,
  boxHeight: 7,
  multiKeyBackground: "rgba(255, 255, 255, 0.14)",
  callbacks,
});

const createChartTooltipCallbacks = ({
  chartLabels,
  data,
  curvaSelecionada = "",
  idMedsArray = null,
  valueSuffix = "",
  getValueSuffix = null,
  sourceIndices = null,
}) => {
  const primaryDatasetLabels = ["UMIDADE", "TEMPERATURA", "DENSIDADE", "PH"];

  const getGrainForIndex = (dataIndex) => {
    const pointLabel = chartLabels[dataIndex];
    const item = pointLabel
      ? data.find(
          (entry) =>
            entry?.date &&
            dateFormat(entry.date, "UTC:dd/mm/yy' 'HH:MM") === pointLabel
        )
      : null;

    return item?.grain || curvaSelecionada || "";
  };

  return createMetadataTooltipCallbacks({
    chartLabels,
    data,
    sourceIndices,
    primaryDatasetLabels,
    extraCallbacks: {
      label(context) {
        let label = context.dataset.label || "";
        if (label) {
          label += ": ";
        }
        if (context.parsed.y !== null) {
          const suffix = getValueSuffix
            ? getValueSuffix(context.dataset.label)
            : valueSuffix;
          label += `${context.parsed.y}${suffix}`;
        }

        if (!primaryDatasetLabels.includes(context.dataset.label)) {
          return label;
        }

        const lines = [label];

        if (
          context.dataset.label === "UMIDADE" &&
          idMedsArray?.[context.dataIndex]
        ) {
          lines.push(`${idMedsArray[context.dataIndex]}`);
        }

        if (context.dataset.label === "UMIDADE") {
          const grain = getGrainForIndex(context.dataIndex);
          if (grain) {
            lines.push(grain);
          }
        }

        return lines.length > 1 ? lines : label;
      },
    },
  });
};

const Grafico = (props) => {
  const {
    data,
    curvaSelecionada = "",
    grainOperation,
  } = props;

  const [humidity, setHumidity] = useState({
    labels: [],
    datasets: []
  })

  const [optionshumidity, setOptionshumidity] = useState(null)

  const [ph, setPh] = useState({
    labels: [],
    datasets: []
  })

  const [optionsph, setOptionsph] = useState(null)

  const [temperature, setTemperature] = useState({
    labels: [],
    datasets: []
  })

  const [optionstemperature, setOptionstemperature] = useState(null)

  const [chartViewMode, setChartViewMode] = useState(getInitialChartViewMode);

  const [combinedHumidityTemperature, setCombinedHumidityTemperature] = useState({
    labels: [],
    datasets: [],
  });
  const [optionsCombinedHT, setOptionsCombinedHT] = useState(null);

  const [combinedAllMetrics, setCombinedAllMetrics] = useState({
    labels: [],
    datasets: [],
  });
  const [optionsCombinedAll, setOptionsCombinedAll] = useState(null);

  const [endValueTemperature, setEndValueTemperature] = useState(0)
  const [endValueHumidity, setEndValueHumidity] = useState(0)
  const [endValuePh, setEndValuePh] = useState(0)

  // Filtros de gráfico (UI removida; mantidos desligados por padrão)
  const movingAverageActive = MOVING_AVERAGE_ACTIVE;
  const movingAveragePeriod = MOVING_AVERAGE_PERIOD;
  const exponentialFilterActive = EXPONENTIAL_FILTER_ACTIVE;
  const zScoreFilterActive = Z_SCORE_FILTER_ACTIVE;

  const filterAlpha = 0.4;
  const outlierThreshold = 5;
  const zScoreThreshold = 1.5;

  const humidityChartRef = useRef(null);
  const phChartRef = useRef(null);
  const temperatureChartRef = useRef(null);
  const humidityChartRefMobile = useRef(null);
  const phChartRefMobile = useRef(null);
  const temperatureChartRefMobile = useRef(null);
  const combinedHTChartRef = useRef(null);
  const combinedHTChartRefMobile = useRef(null);
  const combinedAllChartRef = useRef(null);
  const combinedAllChartRefMobile = useRef(null);

  const chartSourceIndicesRef = useRef([]);
  const selectedSourceIndicesRef = useRef(new Set());
  const suppressNextClickRef = useRef(false);
  const dragStateRef = useRef({
    chart: null,
    startX: 0,
    currentX: 0,
    isDragging: false,
  });
  const [selectedSourceIndices, setSelectedSourceIndices] = useState(() => new Set());
  const [metadataModalMeasure, setMetadataModalMeasure] = useState(null);
  const [pointActionContext, setPointActionContext] = useState(null);

  const selectedCount = selectedSourceIndices.size;

  const toggleSourceIndexSelection = useCallback((sourceIndex) => {
    setSelectedSourceIndices((prev) => {
      const next = new Set(prev);
      if (next.has(sourceIndex)) next.delete(sourceIndex);
      else next.add(sourceIndex);
      return next;
    });
  }, []);

  const chartRefList = useMemo(
    () => [
      humidityChartRef,
      phChartRef,
      temperatureChartRef,
      humidityChartRefMobile,
      phChartRefMobile,
      temperatureChartRefMobile,
      combinedHTChartRef,
      combinedHTChartRefMobile,
      combinedAllChartRef,
      combinedAllChartRefMobile,
    ],
    []
  );

  const resizeAllCharts = useCallback(() => {
    requestAnimationFrame(() => {
      chartRefList.forEach((ref) => {
        ref.current?.resize?.();
      });
    });
  }, [chartRefList]);

  const getSelectedPointStyle = useCallback((dataIndex) => {
    const srcIdx = chartSourceIndicesRef.current?.[dataIndex];
    if (srcIdx === undefined || !selectedSourceIndicesRef.current.has(srcIdx)) {
      return null;
    }
    return { radius: 7, bg: "#1c4220", border: "#8fad15" };
  }, []);

  const resolveSourceIndexFromChart = useCallback((chart, dataIndex) => {
    const fromHumidityMap = chartSourceIndicesRef.current?.[dataIndex];
    if (fromHumidityMap !== undefined && fromHumidityMap >= 0) {
      return fromHumidityMap;
    }

    const label = chart.data?.labels?.[dataIndex];
    if (!label || !data?.length) return undefined;

    const idx = data.findIndex(
      (item) => item?.date && dateFormat(item.date, "UTC:dd/mm/yy' 'HH:MM") === label
    );
    return idx >= 0 ? idx : undefined;
  }, [data]);

  const handleChartClick = useCallback((elements, chart) => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }

    if (!chart || !elements?.length) return;

    const primaryDatasetLabels = ["UMIDADE", "TEMPERATURA", "DENSIDADE", "PH"];
    const primaryElement = elements.find((element) => {
      const label = chart.data.datasets[element.datasetIndex]?.label;
      return primaryDatasetLabels.includes(label);
    });
    const { index: dataIndex } = primaryElement || elements[0];

    const sourceIndex = resolveSourceIndexFromChart(chart, dataIndex);
    if (sourceIndex === undefined) return;

    const clickedMeasure = data?.[sourceIndex];
    if (hasMeasureMetadata(clickedMeasure)) {
      setPointActionContext({ measure: clickedMeasure, sourceIndex });
      return;
    }

    toggleSourceIndexSelection(sourceIndex);
  }, [resolveSourceIndexFromChart, data, toggleSourceIndexSelection]);

  const handlePointActionClose = useCallback(() => {
    setPointActionContext(null);
  }, []);

  const handlePointActionOpenCalculations = useCallback(() => {
    if (pointActionContext?.sourceIndex === undefined) return;
    toggleSourceIndexSelection(pointActionContext.sourceIndex);
    setPointActionContext(null);
  }, [pointActionContext, toggleSourceIndexSelection]);

  const handlePointActionOpenDatasetDetails = useCallback(() => {
    if (!pointActionContext?.measure) return;
    setMetadataModalMeasure(pointActionContext.measure);
    setPointActionContext(null);
  }, [pointActionContext]);

  const calculateSelectedAverage = useCallback((items, key) => {
    const validValues = items
      .map((item) => Number(item[key]))
      .filter((value) => !isNaN(value));
    return validValues.length > 0
      ? validValues.reduce((sum, value) => sum + value, 0) / validValues.length
      : 0;
  }, []);

  const selectionResult = useMemo(() => {
    const indices = [...selectedSourceIndices];
    if (!indices.length || !data?.length) return null;

    const selected = indices.map((i) => data[i]).filter(Boolean);
    const grain = curvaSelecionada || selected[selected.length - 1]?.grain;

    if (!grain) {
      return {
        analysis: null,
        error: "Não foi possível identificar a curva do grão nos pontos selecionados.",
        count: selected.length,
      };
    }

    try {
      const analysis = analyzeGrain({
        grain,
        operation: getMeasureOperationId(selected[selected.length - 1]) || grainOperation,
        humidity: calculateSelectedAverage(selected, "humidity"),
        temperature: calculateSelectedAverage(selected, "temperature"),
        density: calculateSelectedAverage(selected, "ph"),
        ph: calculateSelectedAverage(selected, "ph"),
        weight: calculateSelectedAverage(selected, "peso"),
      });
      return { analysis, error: null, count: selected.length };
    } catch (err) {
      return {
        analysis: null,
        error: err?.message || "Não foi possível analisar os pontos selecionados.",
        count: selected.length,
      };
    }
  }, [
    selectedSourceIndices,
    data,
    curvaSelecionada,
    grainOperation,
    calculateSelectedAverage,
  ]);

  const clearSelection = useCallback(() => {
    setSelectedSourceIndices(new Set());
  }, []);

  const handleChartViewModeChange = useCallback((mode) => {
    setChartViewMode(mode);
    setSelectedSourceIndices(new Set());
    try {
      localStorage.setItem(CHART_VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // ignore storage errors
    }
  }, []);

  chartDragSelectBridge.handlers = {
    onDragStart(chart, x) {
      dragStateRef.current = {
        chart,
        startX: x,
        currentX: x,
        isDragging: false,
      };
    },
    onDragMove(chart, x) {
      const state = dragStateRef.current;
      if (!state.chart || state.chart !== chart) return;

      state.currentX = x;
      if (!state.isDragging && Math.abs(x - state.startX) >= DRAG_SELECT_THRESHOLD) {
        state.isDragging = true;
      }
      if (state.isDragging) {
        chart.draw();
      }
    },
    onDragEnd(chart, x) {
      const state = dragStateRef.current;
      if (!state.chart || state.chart !== chart) return;

      if (state.isDragging) {
        const minX = Math.min(state.startX, x);
        const maxX = Math.max(state.startX, x);
        const chartIndices = getChartIndicesInPixelRange(chart, minX, maxX);
        const sourceIndices = [
          ...new Set(
            chartIndices
              .map((index) => resolveSourceIndexFromChart(chart, index))
              .filter((index) => index !== undefined)
          ),
        ];

        if (sourceIndices.length) {
          setSelectedSourceIndices((prev) => {
            const next = new Set(prev);
            sourceIndices.forEach((index) => {
              if (next.has(index)) next.delete(index);
              else next.add(index);
            });
            return next;
          });
        }

        suppressNextClickRef.current = true;
        chart.draw();
      }

      dragStateRef.current = {
        chart: null,
        startX: 0,
        currentX: 0,
        isDragging: false,
      };
    },
    getDragRect(chart) {
      const state = dragStateRef.current;
      if (!state.isDragging || state.chart !== chart) return null;

      return {
        left: Math.min(state.startX, state.currentX),
        right: Math.max(state.startX, state.currentX),
      };
    },
    isDraggingChart(chart) {
      const state = dragStateRef.current;
      return state.chart === chart && state.isDragging;
    },
  };

  useEffect(() => () => {
    chartDragSelectBridge.handlers = null;
  }, []);

  useEffect(() => {
    selectedSourceIndicesRef.current = selectedSourceIndices;
    chartRefList.forEach((ref) => ref.current?.update("none"));
  }, [selectedSourceIndices, chartRefList]);

  useEffect(() => {
    setSelectedSourceIndices(new Set());
  }, [data]);

  const handleChartsSectionExpand = useCallback(() => {
    resizeAllCharts();
    window.setTimeout(resizeAllCharts, 150);
  }, [resizeAllCharts]);

  useEffect(() => {
    if (
      !humidity?.datasets?.length &&
      !ph?.datasets?.length &&
      !temperature?.datasets?.length &&
      !combinedHumidityTemperature?.datasets?.length &&
      !combinedAllMetrics?.datasets?.length
    ) {
      return;
    }
    resizeAllCharts();
  }, [
    humidity,
    ph,
    temperature,
    combinedHumidityTemperature,
    combinedAllMetrics,
    optionshumidity,
    optionsph,
    optionstemperature,
    optionsCombinedHT,
    optionsCombinedAll,
    chartViewMode,
    resizeAllCharts,
  ]);


  const handleZoom = (sourceChart) => {
    if (!sourceChart) return;
    const { min, max } = sourceChart.scales.x;

    const allCharts = [
      humidityChartRef.current, phChartRef.current, temperatureChartRef.current,
      humidityChartRefMobile.current, phChartRefMobile.current, temperatureChartRefMobile.current,
      combinedHTChartRef.current, combinedHTChartRefMobile.current,
      combinedAllChartRef.current, combinedAllChartRefMobile.current,
    ];

    allCharts.forEach(chart => {
      if (chart && chart !== sourceChart) {
        chart.options.scales.x.min = min;
        chart.options.scales.x.max = max;
        chart.update('none');
      }
    });
  };

  const handleGetValue = useCallback(() => {
    if (data.length) {
      if (data[data.length - 1].temperature) {
        setEndValueTemperature(data[data.length - 1].temperature)
      } else {
        setEndValueTemperature()
      }

      if (data[data.length - 1].humidity) {
        setEndValueHumidity(data[data.length - 1].humidity)
      } else {
        setEndValueHumidity()
      }

      if (data[data.length - 1].unidadePh) {
        setEndValuePh(data[data.length - 1].unidadePh.replace(/\s/g, ''))
      } else if (data[data.length - 1].ph) {
        setEndValuePh(data[data.length - 1].ph)
      } else {
        setEndValuePh()
      }
    }
  }, [data]);

  const getBackgroundColor = (category) => {
    switch (category) {
      case 'P_0M5':
        return '#febf97';
      case 'P_0M3':
        return '#f8f8d6';
      case 'P_0':
        return '#56c24b';
      case 'P_0P5':
        return '#b8faff';
      case 'P_0P75':
        return '#81d6f8';
      case 'P_1P':
        return '#8cb0b0';
      case 'P_1P5':
        return '#537979';
      case 'P_2P':
        return '#ed7615';
      default:
        return 'grey';
    }
  };


  const formatHumidity = (data) => {
    if (!data || (!data.humidity && !data.humidityReal)) return null;

    const humidityValue =
      data?.humidity && (data?.applyGain === "P_0" || !data.applyGain)
        ? parseFloat(data.humidity).toFixed(1)
        : parseFloat(data.humidityReal).toFixed(1);

    return humidityValue;
  };

  // Funções para cálculo das médias móveis
  const calculateSimpleMovingAverage = (dataArray, period) => {
    return dataArray.map((_, index) => {
      if (index < period - 1) return null;
      const slice = dataArray.slice(index - period + 1, index + 1);
      const validValues = slice.filter(val => val !== null && !isNaN(val));
      if (validValues.length === 0) return null;
      const sum = validValues.reduce((acc, val) => acc + parseFloat(val), 0);
      return (sum / validValues.length).toFixed(1);
    });
  };

  const calculateExponentialMovingAverage = (dataArray, period) => {
    const multiplier = 2 / (period + 1);
    const emaArray = [];

    for (let i = 0; i < dataArray.length; i++) {
      const currentValue = dataArray[i];
      if (currentValue === null || isNaN(currentValue)) {
        emaArray.push(null);
        continue;
      }

      if (i === 0) {
        emaArray.push(parseFloat(currentValue).toFixed(1));
      } else {
        const prevEma = emaArray[i - 1];
        if (prevEma !== null) {
          const ema = (parseFloat(currentValue) * multiplier) + (parseFloat(prevEma) * (1 - multiplier));
          emaArray.push(ema.toFixed(1));
        } else {
          emaArray.push(parseFloat(currentValue).toFixed(1));
        }
      }
    }

    return emaArray;
  };

  const calculateWeightedMovingAverage = (dataArray, period) => {
    return dataArray.map((_, index) => {
      if (index < period - 1) return null;
      const slice = dataArray.slice(index - period + 1, index + 1);
      const validIndices = [];
      const validValues = [];

      slice.forEach((val, i) => {
        if (val !== null && !isNaN(val)) {
          validIndices.push(i + 1);
          validValues.push(parseFloat(val));
        }
      });

      if (validValues.length === 0) return null;

      const weightedSum = validValues.reduce((acc, val, i) => acc + (val * validIndices[i]), 0);
      const weightSum = validIndices.reduce((acc, weight) => acc + weight, 0);

      return (weightedSum / weightSum).toFixed(1);
    });
  };

  // Função para calcular estatísticas básicas
  const calculateStats = (dataArray) => {
    const validData = dataArray.filter(val => val !== null && !isNaN(val)).map(val => parseFloat(val));
    if (validData.length === 0) return { mean: 0, stdDev: 0 };

    const mean = validData.reduce((sum, val) => sum + val, 0) / validData.length;
    const variance = validData.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / validData.length;
    const stdDev = Math.sqrt(variance);

    return { mean, stdDev };
  };

  // Função para filtro exponencial com detecção de outliers
  const applyExponentialFilter = (dataArray, alpha = 0.4, threshold = 5) => {
    if (!dataArray || dataArray.length === 0) return [];

    const { mean, stdDev } = calculateStats(dataArray);
    const filteredData = [];
    let filteredValue = null;

    for (let i = 0; i < dataArray.length; i++) {
      const currentValue = dataArray[i];

      if (currentValue === null || isNaN(currentValue)) {
        filteredData.push(null);
        continue;
      }

      const numValue = parseFloat(currentValue);

      // Detecção de outlier
      const isOutlier = Math.abs(numValue - mean) > threshold * stdDev;

      if (i === 0) {
        // Primeiro valor
        filteredValue = isOutlier ? mean : numValue;
      } else {
        // Aplicar filtro exponencial apenas se não for outlier
        if (isOutlier) {
          // Mantém o valor filtrado anterior
        } else {
          // Filtro exponencial: y[n] = α * x[n] + (1-α) * y[n-1]
          filteredValue = alpha * numValue + (1 - alpha) * filteredValue;
        }
      }

      filteredData.push(filteredValue.toFixed(1));
    }

    return filteredData;
  };

  // Função para calcular Z-score com janela deslizante (mais sensível a variações locais)
  const calculateZScoreWithWindow = (dataArray, windowSize = 10) => {
    const zScores = [];

    for (let i = 0; i < dataArray.length; i++) {
      const val = dataArray[i];
      if (val === null || isNaN(val)) {
        zScores.push(null);
        continue;
      }

      // Definir janela ao redor do ponto atual
      const start = Math.max(0, i - Math.floor(windowSize / 2));
      const end = Math.min(dataArray.length, i + Math.floor(windowSize / 2) + 1);

      // Extrair valores válidos na janela
      const windowData = dataArray.slice(start, end)
        .filter(v => v !== null && !isNaN(v))
        .map(v => parseFloat(v));

      if (windowData.length < 3) {
        zScores.push(0); // Não há dados suficientes para calcular
        continue;
      }

      // Calcular média e desvio padrão da janela
      const mean = windowData.reduce((sum, v) => sum + v, 0) / windowData.length;
      const variance = windowData.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / windowData.length;
      const stdDev = Math.sqrt(variance);

      // Calcular Z-score do ponto atual
      const numVal = parseFloat(val);
      const zScore = stdDev > 0 ? Math.abs((numVal - mean) / stdDev) : 0;
      zScores.push(zScore);
    }

    return zScores;
  };

  // Função para calcular Z-score e filtrar outliers (versão melhorada)
  const calculateZScore = (dataArray) => {
    const validData = dataArray.filter(val => val !== null && !isNaN(val)).map(val => parseFloat(val));
    if (validData.length === 0) return { mean: 0, stdDev: 0, zScores: [] };

    const mean = validData.reduce((sum, val) => sum + val, 0) / validData.length;
    const variance = validData.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / validData.length;
    const stdDev = Math.sqrt(variance);

    // Usar janela deslizante para detectar variações locais
    const zScores = calculateZScoreWithWindow(dataArray, 8);

    return { mean, stdDev, zScores };
  };

  // Função para detectar mudanças bruscas baseada em diferença percentual
  const detectAbruptChanges = (dataArray, percentThreshold = 20) => {
    const outlierFlags = [];

    for (let i = 0; i < dataArray.length; i++) {
      const val = dataArray[i];
      if (val === null || isNaN(val)) {
        outlierFlags.push(false);
        continue;
      }

      let isOutlier = false;
      const numVal = parseFloat(val);

      // Verificar diferença com valor anterior
      if (i > 0 && dataArray[i - 1] !== null && !isNaN(dataArray[i - 1])) {
        const prevVal = parseFloat(dataArray[i - 1]);
        const percentDiff = Math.abs((numVal - prevVal) / prevVal) * 100;
        if (percentDiff > percentThreshold) {
          isOutlier = true;
        }
      }

      // Verificar diferença com próximo valor
      if (i < dataArray.length - 1 && dataArray[i + 1] !== null && !isNaN(dataArray[i + 1])) {
        const nextVal = parseFloat(dataArray[i + 1]);
        const percentDiff = Math.abs((numVal - nextVal) / nextVal) * 100;
        if (percentDiff > percentThreshold) {
          isOutlier = true;
        }
      }

      outlierFlags.push(isOutlier);
    }

    return outlierFlags;
  };

  // Função para remover outliers com Z-score combinado com detecção de mudanças bruscas
  const filterOutliersZScoreWithContinuity = (dataArray, labels, idMeds, sourceIndices, threshold = 1.5) => {
    const { zScores } = calculateZScore(dataArray);
    const abruptChanges = detectAbruptChanges(dataArray, 20); // 20% de diferença

    const filteredData = [];
    const filteredLabels = [];
    const filteredIdMeds = [];
    const filteredSourceIndices = [];

    for (let i = 0; i < dataArray.length; i++) {
      const val = dataArray[i];
      if (val === null || isNaN(val)) {
        continue; // Pula valores nulos/inválidos
      }

      const zScore = zScores[i];
      const isAbruptChange = abruptChanges[i];

      // Considerar outlier se Z-score alto OU mudança brusca
      const isOutlier = (zScore !== null && zScore > threshold) || isAbruptChange;

      if (!isOutlier) {
        filteredData.push(val);
        filteredLabels.push(labels[i]);
        if (idMeds) filteredIdMeds.push(idMeds[i]);
        if (sourceIndices) filteredSourceIndices.push(sourceIndices[i]);
        else filteredSourceIndices.push(i);
      }
      // Outliers são simplesmente ignorados (não adicionados)
    }

    return { filteredData, filteredLabels, filteredIdMeds, filteredSourceIndices };
  };

  const updateInfoGraphics = async () => {
    let labels = await data.map(data => dateFormat(data.date, "UTC:dd/mm/yy' 'HH:MM"));
    let idMeds = data.map(d => d.idMed);

    // Preparar dados de umidade
    let humidityData = data.map((data) => formatHumidity(data));
    let humidityLabels = labels;
    let humidityIdMeds = idMeds;
    let humiditySourceIndices = data.map((_, index) => index);

    // Aplicar filtro Z-score se ativo
    if (zScoreFilterActive) {
      const filtered = filterOutliersZScoreWithContinuity(
        humidityData,
        labels,
        idMeds,
        humiditySourceIndices,
        zScoreThreshold
      );
      humidityData = filtered.filteredData;
      humidityLabels = filtered.filteredLabels;
      humidityIdMeds = filtered.filteredIdMeds;
      humiditySourceIndices = filtered.filteredSourceIndices;
    }

    chartSourceIndicesRef.current = humiditySourceIndices;

    const humidityPointStyleProps = buildChartPointStyleProps({
      chartLabels: humidityLabels,
      data,
      sourceIndices: humiditySourceIndices,
      getSelectedPointStyle,
    });

    const chartOnClick = (_event, elements, chart) => handleChartClick(elements, chart);

    // Datasets base para umidade
    const baseHumidityDatasets = [
      {
        labels: humidityLabels,
        label: `TOLERÂNCIA`,
        data: zScoreFilterActive
          ? []
          : data.map((item) => {
            const humidity = parseFloat(item?.humidity);
            const humidityReal = parseFloat(item?.humidityReal);
            const gain = item?.applyGain;

            // 1. Validação de existência básica
            if (!item?.humidityReal || !gain || gain === "P_0" || gain === "") {
              return null;
            }

            // 2. Regra de Exceção: Se for P_0M3 ou P_0M5, mostra sempre
            const isSpecialGain = gain === "P_0M3" || gain === "P_0M5";

            // 3. Regra Geral: Se não for especial, precisa ser maior ou igual à real
            if (isSpecialGain || humidity >= humidityReal) {
              return humidity.toFixed(1);
            }

            return null;
          }),
        borderColor: zScoreFilterActive
          ? []
          : data.map((item) => getBackgroundColor(item.applyGain)),
        borderWidth: 2,
        fill: true,
        pointRadius: 2,
        tension: 0.3,
        datalabels: {
          labels: {
            title: {
              color: "grey",
            }
          },
          display: 'auto',
          align: 'end',
          font: {
            weight: 600,
            size: 11,
          }
        },
      },
    ];

    // Adicionar dataset principal de umidade
    baseHumidityDatasets.push({
      labels: humidityLabels,
      label: `UMIDADE`,
      data: humidityData,
      borderColor: function (context) {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        if (!chartArea) {
          return;
        }
        return getGradient(ctx, chartArea);
      },
      borderWidth: 3,
      fill: true,
      tension: 0.3,
      datalabels: buildSeparateDataLabels("%", "bottom", "#1c4220"),
      ...humidityPointStyleProps,
    });

    // Aplicar filtro exponencial se ativo
    let processedHumidityData = humidityData;
    if (exponentialFilterActive) {
      const filteredData = applyExponentialFilter(humidityData, filterAlpha, outlierThreshold);
      baseHumidityDatasets.push({
        label: `Filtro Exponencial (α=${filterAlpha}, limiar=${outlierThreshold})`,
        data: filteredData,
        borderColor: '#00CED1',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [2, 2],
        datalabels: { display: false }
      });
      processedHumidityData = filteredData;
    }

    if (movingAverageActive.simple) {
      const smaData = calculateSimpleMovingAverage(processedHumidityData, movingAveragePeriod);
      baseHumidityDatasets.push({
        label: `Média Móvel Simples (${movingAveragePeriod})`,
        data: smaData,
        borderColor: '#DC143C',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [5, 5],
        datalabels: { display: false }
      });
    }

    if (movingAverageActive.exponential) {
      const emaData = calculateExponentialMovingAverage(processedHumidityData, movingAveragePeriod);
      baseHumidityDatasets.push({
        label: `Média Móvel Exponencial (${movingAveragePeriod})`,
        data: emaData,
        borderColor: '#FF8C00',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [10, 5],
        datalabels: { display: false }
      });
    }

    if (movingAverageActive.weighted) {
      const wmaData = calculateWeightedMovingAverage(processedHumidityData, movingAveragePeriod);
      baseHumidityDatasets.push({
        label: `Média Móvel Ponderada (${movingAveragePeriod})`,
        data: wmaData,
        borderColor: '#8A2BE2',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [15, 5],
        datalabels: { display: false }
      });
    }

    const humidityLabel = {
      labels: humidityLabels,
      datasets: baseHumidityDatasets
    }
    setHumidity(humidityLabel)

    let number = humidityData.filter(val => val !== null && !isNaN(val)).map(val => parseFloat(val))
    let minValue = number.length > 0 ? Math.min(...number) : 0
    let maxValue = number.length > 0 ? Math.max(...number) : 100
    let delayed;
    const optionsHumididy = {
      responsive: true,
      maintainAspectRatio: false,
      onClick: chartOnClick,
      onHover: (event, activeElements, chart) => {
        handleHover(chart, activeElements);
      },
      scales: {
        y: {
          min: minValue -= (minValue * 30) / 100,
          max: maxValue += (maxValue * 30) / 100,
          stepSize: 5,
        },
        x: {},
      },
      animations: {
        radius: {
          duration: 500,
          easing: 'linear',
          loop: (context) => context.active
        }
      },
      animation: {
        onComplete: () => {
          delayed = true;
        },
        delay: (context) => {
          const totalDataPoints = context.chart.data.datasets.reduce((sum, dataset) => sum + dataset.data.length, 0);

          if (totalDataPoints > 5000) {
            return 0; // Sem delay se houver mais de 5000 pontos
          }

          let delay = 0;
          if (context.type === 'data' && context.mode === 'default' && !delayed) {
            delay = context.dataIndex * 5 + context.datasetIndex * 2;
          }
          return delay;
        },
      },
      hoverRadius: 6,
      hoverBackgroundColor: 'yellow',
      interaction: {
        mode: 'nearest',
        intersect: false,
        axis: 'x'
      },
      plugins: {
        zoom: {
          pan: {
            enabled: false,
            mode: 'x',
          },
          zoom: {
            wheel: {
              enabled: false,
            },
            pinch: {
              enabled: false,
            },
            mode: 'x',
            onZoomComplete: ({ chart }) => {
              handleZoom(chart);
            }
          }
        },
        tooltip: buildChartTooltipOptions(
          createChartTooltipCallbacks({
            chartLabels: humidityLabels,
            data,
            curvaSelecionada,
            idMedsArray: humidityIdMeds,
            sourceIndices: humiditySourceIndices,
            getValueSuffix: (datasetLabel) => {
              const suffix = getMetricTooltipSuffix(datasetLabel, data);
              return suffix || "%";
            },
          })
        ),
        title: {
          display: true,
          text: '',
        }
      }
    };
    setOptionshumidity(optionsHumididy)

    //----------------------------------------------------------------

    // Preparar dados de pH
    let phData = data.map((data) => (parseFloat(data.ph).toFixed(1)));
    let phLabels = labels;

    // Aplicar filtro Z-score se ativo
    if (zScoreFilterActive) {
      const filtered = filterOutliersZScoreWithContinuity(phData, labels, idMeds, null, zScoreThreshold);
      phData = filtered.filteredData;
      phLabels = filtered.filteredLabels;
    }

    const phSourceIndices = zScoreFilterActive
      ? phLabels.map((label) =>
          data.findIndex(
            (item) =>
              item?.date &&
              dateFormat(item.date, "UTC:dd/mm/yy' 'HH:MM") === label
          )
        )
      : data.map((_, index) => index);

    const phPointStyleProps = buildChartPointStyleProps({
      chartLabels: phLabels,
      data,
      sourceIndices: phSourceIndices,
      getSelectedPointStyle: () => null,
    });

    // Datasets base para pH/densidade
    const basePhDatasets = [];
    const phDatasetLabel =
      data.length &&
      data[data.length - 1].unidadePh &&
      data[data.length - 1].unidadePh.includes("Kg/m³")
        ? "DENSIDADE"
        : "PH";
    const phDataLabelSuffix = getDensityDataLabelSuffix(data);

    // Adicionar dataset principal de pH/densidade
    basePhDatasets.push({
      labels: phLabels,
      label: phDatasetLabel,
      data: phData,
      borderColor: function (context) {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        if (!chartArea) {
          return;
        }
        return getGradient(ctx, chartArea);
      },
      borderWidth: 3,
      fill: true,
      tension: 0.3,
      datalabels: buildSeparateDataLabels(phDataLabelSuffix, "end", "#1B5D43"),
      ...phPointStyleProps,
    });

    // Aplicar filtro exponencial se ativo
    let processedPhData = phData;
    if (exponentialFilterActive) {
      const filteredData = applyExponentialFilter(phData, filterAlpha, outlierThreshold);
      basePhDatasets.push({
        label: `Filtro Exponencial (α=${filterAlpha}, limiar=${outlierThreshold})`,
        data: filteredData,
        borderColor: '#00CED1',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [2, 2],
        datalabels: { display: false }
      });
      processedPhData = filteredData;
    }

    if (movingAverageActive.simple) {
      const smaData = calculateSimpleMovingAverage(processedPhData, movingAveragePeriod);
      basePhDatasets.push({
        label: `Média Móvel Simples (${movingAveragePeriod})`,
        data: smaData,
        borderColor: '#DC143C',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [5, 5],
        datalabels: { display: false }
      });
    }

    if (movingAverageActive.exponential) {
      const emaData = calculateExponentialMovingAverage(processedPhData, movingAveragePeriod);
      basePhDatasets.push({
        label: `Média Móvel Exponencial (${movingAveragePeriod})`,
        data: emaData,
        borderColor: '#FF8C00',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [10, 5],
        datalabels: { display: false }
      });
    }

    if (movingAverageActive.weighted) {
      const wmaData = calculateWeightedMovingAverage(processedPhData, movingAveragePeriod);
      basePhDatasets.push({
        label: `Média Móvel Ponderada (${movingAveragePeriod})`,
        data: wmaData,
        borderColor: '#8A2BE2',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [15, 5],
        datalabels: { display: false }
      });
    }

    const phLabel = {
      labels: phLabels,
      datasets: basePhDatasets
    }
    setPh(phLabel)

    number = phData.filter(val => val !== null && !isNaN(val)).map(val => parseFloat(val))
    minValue = number.length > 0 ? Math.min(...number) : 0
    maxValue = number.length > 0 ? Math.max(...number) : 14

    const optionsph = {
      responsive: true,
      maintainAspectRatio: false,
      onClick: chartOnClick,
      onHover: (event, activeElements, chart) => {
        handleHover(chart, activeElements);
      },
      scales: {
        y: {
          min: minValue -= (minValue * 30) / 100,
          max: maxValue += (maxValue * 30) / 100,
          stepSize: 5,
        },
        x: {},
      },
      animations: {
        radius: {
          duration: 500,
          easing: 'linear',
          loop: (context) => context.active
        }
      },
      animation: {
        onComplete: () => {
          delayed = true;
        },
        delay: (context) => {
          const totalDataPoints = context.chart.data.datasets.reduce((sum, dataset) => sum + dataset.data.length, 0);

          if (totalDataPoints > 5000) {
            return 0; // Sem delay se houver mais de 5000 pontos
          }

          let delay = 0;
          if (context.type === 'data' && context.mode === 'default' && !delayed) {
            delay = context.dataIndex * 5 + context.datasetIndex * 2;
          }
          return delay;
        },
      },
      hoverRadius: 6,
      hoverBackgroundColor: 'yellow',
      interaction: {
        mode: 'nearest',
        intersect: false,
        axis: 'x'
      },
      plugins: {
        zoom: {
          pan: {
            enabled: false,
            mode: 'x',
          },
          zoom: {
            wheel: {
              enabled: false,
            },
            pinch: {
              enabled: false,
            },
            mode: 'x',
            onZoomComplete: ({ chart }) => {
              handleZoom(chart);
            }
          }
        },
        tooltip: buildChartTooltipOptions(
          createChartTooltipCallbacks({
            chartLabels: phLabels,
            data,
            curvaSelecionada,
            sourceIndices: phSourceIndices,
            getValueSuffix: (datasetLabel) => getMetricTooltipSuffix(datasetLabel, data),
          })
        ),
        title: {
          display: true,
          text: '',
        }
      }
    };
    setOptionsph(optionsph)

    //----------------------------------------------------------------
    // Preparar dados de temperatura
    let temperatureData = data.map((data) => (data.temperature ? parseFloat(data.temperature).toFixed(1) : null));
    let temperatureLabels = labels;

    // Aplicar filtro Z-score se ativo
    if (zScoreFilterActive) {
      const filtered = filterOutliersZScoreWithContinuity(temperatureData, labels, idMeds, null, zScoreThreshold);
      temperatureData = filtered.filteredData;
      temperatureLabels = filtered.filteredLabels;
    }

    const temperatureSourceIndices = zScoreFilterActive
      ? temperatureLabels.map((label) =>
          data.findIndex(
            (item) =>
              item?.date &&
              dateFormat(item.date, "UTC:dd/mm/yy' 'HH:MM") === label
          )
        )
      : data.map((_, index) => index);

    const temperaturePointStyleProps = buildChartPointStyleProps({
      chartLabels: temperatureLabels,
      data,
      sourceIndices: temperatureSourceIndices,
      getSelectedPointStyle: () => null,
    });

    // Datasets base para temperatura
    const baseTemperatureDatasets = [];

    // Adicionar dataset principal de temperatura
    baseTemperatureDatasets.push({
      labels: temperatureLabels,
      label: 'TEMPERATURA',
      data: temperatureData,
      borderColor: function (context) {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        if (!chartArea) {
          return;
        }
        return getGradient(ctx, chartArea, true);
      },
      borderWidth: 3,
      fill: true,
      tension: 0.3,
      datalabels: buildSeparateDataLabels("°C", "end", "#E52207"),
      ...temperaturePointStyleProps,
    });

    // Aplicar filtro exponencial se ativo
    let processedTemperatureData = temperatureData;
    if (exponentialFilterActive) {
      const filteredData = applyExponentialFilter(temperatureData, filterAlpha, outlierThreshold);
      baseTemperatureDatasets.push({
        label: `Filtro Exponencial (α=${filterAlpha}, limiar=${outlierThreshold})`,
        data: filteredData,
        borderColor: '#00CED1',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [2, 2],
        datalabels: { display: false }
      });
      processedTemperatureData = filteredData;
    }

    if (movingAverageActive.simple) {
      const smaData = calculateSimpleMovingAverage(processedTemperatureData, movingAveragePeriod);
      baseTemperatureDatasets.push({
        label: `Média Móvel Simples (${movingAveragePeriod})`,
        data: smaData,
        borderColor: '#DC143C',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [5, 5],
        datalabels: { display: false }
      });
    }

    if (movingAverageActive.exponential) {
      const emaData = calculateExponentialMovingAverage(processedTemperatureData, movingAveragePeriod);
      baseTemperatureDatasets.push({
        label: `Média Móvel Exponencial (${movingAveragePeriod})`,
        data: emaData,
        borderColor: '#FF8C00',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [10, 5],
        datalabels: { display: false }
      });
    }

    if (movingAverageActive.weighted) {
      const wmaData = calculateWeightedMovingAverage(processedTemperatureData, movingAveragePeriod);
      baseTemperatureDatasets.push({
        label: `Média Móvel Ponderada (${movingAveragePeriod})`,
        data: wmaData,
        borderColor: '#8A2BE2',
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 1,
        fill: false,
        tension: 0.3,
        borderDash: [15, 5],
        datalabels: { display: false }
      });
    }

    const temperatureLabel = {
      labels: temperatureLabels,
      datasets: baseTemperatureDatasets
    }
    setTemperature(temperatureLabel)

    const tempData = temperatureData.filter(val => val !== null && !isNaN(val)).map(val => parseFloat(val));
    if (tempData.length > 0) {
      minValue = Math.min(...tempData);
      maxValue = Math.max(...tempData);

      const optionstemperature = {
        responsive: true,
        maintainAspectRatio: false,
        onClick: chartOnClick,
        onHover: (event, activeElements, chart) => {
          handleHover(chart, activeElements);
        },
        scales: {
          y: {
            min: minValue -= (minValue * 30) / 100,
            max: maxValue += (maxValue * 30) / 100,
            stepSize: 5,
          },
          x: {},
        },
        animations: {
          radius: {
            duration: 500,
            easing: 'linear',
            loop: (context) => context.active
          }
        },
        animation: {
          onComplete: () => {
            delayed = true;
          },
          delay: (context) => {
            const totalDataPoints = context.chart.data.datasets.reduce((sum, dataset) => sum + dataset.data.length, 0);

            if (totalDataPoints > 5000) {
              return 0; // Sem delay se houver mais de 5000 pontos
            }

            let delay = 0;
            if (context.type === 'data' && context.mode === 'default' && !delayed) {
              delay = context.dataIndex * 5 + context.datasetIndex * 2;
            }
            return delay;
          },
        },
        hoverRadius: 6,
        hoverBackgroundColor: 'yellow',
        interaction: {
          mode: 'nearest',
          intersect: false,
          axis: 'x'
        },
        plugins: {
          zoom: {
            pan: {
              enabled: false,
              mode: 'x',
            },
            zoom: {
              wheel: {
                enabled: false,
              },
              pinch: {
                enabled: false,
              },
              mode: 'x',
              onZoomComplete: ({ chart }) => {
                handleZoom(chart);
              }
            }
          },
          tooltip: buildChartTooltipOptions(
            createChartTooltipCallbacks({
              chartLabels: temperatureLabels,
              data,
              curvaSelecionada,
              sourceIndices: temperatureSourceIndices,
              getValueSuffix: (datasetLabel) => {
                const suffix = getMetricTooltipSuffix(datasetLabel, data);
                return suffix || "°C";
              },
            })
          ),
          title: {
            display: true,
            text: '',
          }
        }
      };
      setOptionstemperature(optionstemperature);
    }

    const comparisonLabels = data.map((item) =>
      dateFormat(item.date, "UTC:dd/mm/yy' 'HH:MM")
    );
    const comparisonIdMeds = data.map((item) => item.idMed);
    const comparisonHumidityData = data.map((item) => formatHumidity(item));
    const comparisonTemperatureData = data.map((item) =>
      item.temperature ? parseFloat(item.temperature).toFixed(1) : null
    );
    const comparisonPhData = data.map((item) =>
      item.ph && !isNaN(parseFloat(item.ph))
        ? parseFloat(item.ph).toFixed(1)
        : null
    );
    const comparisonPhLabel =
      data.length &&
      data[data.length - 1].unidadePh &&
      data[data.length - 1].unidadePh.includes("Kg/m³")
        ? "DENSIDADE"
        : "PH";
    const comparisonPhAxisTitle =
      data.length && data[data.length - 1].unidadePh
        ? data[data.length - 1].unidadePh.replace(/\s/g, "")
        : comparisonPhLabel;

    if (chartViewMode !== "separate") {
      chartSourceIndicesRef.current = comparisonLabels.map((_, index) => index);
    }

    const comparisonPointStyleProps = buildChartPointStyleProps({
      chartLabels: comparisonLabels,
      data,
      sourceIndices: comparisonLabels.map((_, index) => index),
      getSelectedPointStyle,
    });

    const humidityBounds = calcScaleBounds(comparisonHumidityData, 100);
    const temperatureBounds = calcScaleBounds(comparisonTemperatureData, 40);
    const phBounds = calcScaleBounds(comparisonPhData, 14);

    const buildCombinedBaseOptions = () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: chartOnClick,
      onHover: (event, activeElements, chart) => {
        handleHover(chart, activeElements);
      },
      hoverRadius: 6,
      interaction: {
        mode: "nearest",
        intersect: false,
        axis: "x",
      },
      plugins: {
        zoom: {
          pan: {
            enabled: false,
            mode: "x",
          },
          zoom: {
            wheel: {
              enabled: false,
            },
            pinch: {
              enabled: false,
            },
            mode: "x",
            onZoomComplete: ({ chart }) => {
              handleZoom(chart);
            },
          },
        },
      },
    });

    setCombinedHumidityTemperature({
      labels: comparisonLabels,
      datasets: [
        buildHumidityDataset(
          comparisonHumidityData,
          "y",
          comparisonPointStyleProps,
          true
        ),
        buildTemperatureDataset(
          comparisonTemperatureData,
          "y1",
          "top",
          comparisonPointStyleProps
        ),
      ],
    });

    setOptionsCombinedHT({
      ...buildCombinedBaseOptions(),
      scales: {
        x: {},
        y: {
          position: "left",
          min: humidityBounds.min,
          max: humidityBounds.max,
          title: {
            display: true,
            text: "Umidade (%)",
            color: "#1c4220",
            font: { weight: "600", size: 11 },
          },
          ticks: { color: "#1c4220" },
        },
        y1: {
          position: "right",
          min: temperatureBounds.min,
          max: temperatureBounds.max,
          grid: { drawOnChartArea: false },
          title: {
            display: true,
            text: "Temperatura (°C)",
            color: "#E52207",
            font: { weight: "600", size: 11 },
          },
          ticks: { color: "#E52207" },
        },
      },
      plugins: {
        ...buildCombinedBaseOptions().plugins,
        tooltip: buildChartTooltipOptions(
          createChartTooltipCallbacks({
            chartLabels: comparisonLabels,
            data,
            curvaSelecionada,
            idMedsArray: comparisonIdMeds,
            getValueSuffix: (datasetLabel) => getMetricTooltipSuffix(datasetLabel, data),
          })
        ),
      },
    });

    setCombinedAllMetrics({
      labels: comparisonLabels,
      datasets: [
        buildHumidityDataset(
          comparisonHumidityData,
          "y",
          comparisonPointStyleProps,
          true
        ),
        buildTemperatureDataset(
          comparisonTemperatureData,
          "y1",
          "top",
          comparisonPointStyleProps
        ),
        buildPhDataset(
          comparisonPhData,
          comparisonPhLabel,
          "y2",
          "end",
          getDensityDataLabelSuffix(data),
          comparisonPointStyleProps
        ),
      ],
    });

    setOptionsCombinedAll({
      ...buildCombinedBaseOptions(),
      scales: {
        x: {},
        y: {
          position: "left",
          min: humidityBounds.min,
          max: humidityBounds.max,
          title: {
            display: true,
            text: "Umidade (%)",
            color: "#1c4220",
            font: { weight: "600", size: 11 },
          },
          ticks: { color: "#1c4220" },
        },
        y1: {
          position: "right",
          min: temperatureBounds.min,
          max: temperatureBounds.max,
          grid: { drawOnChartArea: false },
          title: {
            display: true,
            text: "Temperatura (°C)",
            color: "#E52207",
            font: { weight: "600", size: 11 },
          },
          ticks: { color: "#E52207" },
        },
        y2: {
          position: "right",
          offset: true,
          min: phBounds.min,
          max: phBounds.max,
          grid: { drawOnChartArea: false },
          title: {
            display: true,
            text: comparisonPhAxisTitle,
            color: "#1B5D43",
            font: { weight: "600", size: 11 },
          },
          ticks: { color: "#1B5D43" },
        },
      },
      plugins: {
        ...buildCombinedBaseOptions().plugins,
        tooltip: buildChartTooltipOptions(
          createChartTooltipCallbacks({
            chartLabels: comparisonLabels,
            data,
            curvaSelecionada,
            idMedsArray: comparisonIdMeds,
            getValueSuffix: (datasetLabel) => getMetricTooltipSuffix(datasetLabel, data),
          })
        ),
      },
    });
  }

  const handleHover = (chart, activeElements) => {
    const allCharts = [
      humidityChartRef.current,
      phChartRef.current,
      temperatureChartRef.current,
      humidityChartRefMobile.current,
      phChartRefMobile.current,
      temperatureChartRefMobile.current,
      combinedHTChartRef.current,
      combinedHTChartRefMobile.current,
      combinedAllChartRef.current,
      combinedAllChartRefMobile.current,
    ].filter(Boolean); // Filtra para remover refs nulas

    if (activeElements.length > 0) {
      const dataIndex = activeElements[0].index;

      allCharts.forEach((c) => {
        if (c !== chart) {
          const active = [];
          c.data.datasets.forEach((dataset, datasetIndex) => {
            if (dataset.data[dataIndex] !== null && dataset.data[dataIndex] !== undefined) {
              active.push({ datasetIndex, index: dataIndex });
            }
          });

          if (c.tooltip) {
            c.tooltip.setActiveElements(active, {});
            c.update();
          }
        }
      });
    } else {
      // Limpa os tooltips de outros gráficos quando o mouse sai
      allCharts.forEach((c) => {
        if (c !== chart && c.tooltip) {
          c.tooltip.setActiveElements([], {});
          c.update();
        }
      });
    }
  };

  useEffect(() => {
    handleGetValue();
    updateInfoGraphics();
    // updateInfoGraphics depende de `data` e handlers já listados abaixo
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recálculo intencional ao mudar medições/gráfico
  }, [
    data,
    curvaSelecionada,
    chartViewMode,
    handleChartClick,
    getSelectedPointStyle,
    handleGetValue,
  ])

  let width, height, gradient;
  let tempGradientWidth, tempGradientHeight, temperatureGradient;

  function getGradient(ctx, chartArea, inverted = false) {
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;

    if (inverted) {
      if (
        !temperatureGradient ||
        tempGradientWidth !== chartWidth ||
        tempGradientHeight !== chartHeight
      ) {
        tempGradientWidth = chartWidth;
        tempGradientHeight = chartHeight;
        temperatureGradient = ctx.createLinearGradient(
          0,
          chartArea.bottom,
          0,
          chartArea.top
        );

        temperatureGradient.addColorStop(0, "#87E8F7");
        temperatureGradient.addColorStop(0.2, "rgba(27, 93, 67, 0.8)");
        temperatureGradient.addColorStop(0.5, "rgba(27, 93, 67, 0.8)");
        temperatureGradient.addColorStop(0.8, "rgba(27, 93, 67, 0.8)");
        temperatureGradient.addColorStop(1, "#E52207");
      }
      return temperatureGradient;
    }

    if (!gradient || width !== chartWidth || height !== chartHeight) {
      width = chartWidth;
      height = chartHeight;
      gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

      gradient.addColorStop(1, "#87E8F7");
      gradient.addColorStop(0.80, "rgba(27, 93, 67, 0.8)");
      gradient.addColorStop(0.50, "rgba(27, 93, 67, 0.8)");
      gradient.addColorStop(0.20, "rgba(27, 93, 67, 0.8)");
      gradient.addColorStop(0, "#E52207");
    }
    return gradient;
  }

  const plugins = {
    afterDraw: function (chart) {
      if (chart.data.datasets.length > 0) {
        // Verificar se TODOS os datasets estão vazios ou se não há dados válidos
        const hasValidData = chart.data.datasets.some(dataset =>
          dataset.data && dataset.data.length > 0 &&
          dataset.data.some(value => value !== null && value !== undefined && !isNaN(value))
        );

        if (!hasValidData) {
          var width = chart.width,
            height = chart.height,
            ctx = chart.ctx;
          ctx.restore();
          var fontSize = (height / 160).toFixed(2);
          ctx.font = fontSize + "em sans-serif";
          ctx.textBaseline = "top";
          var text = "Nenhuma Informação Disponível",
            textX = Math.round((width - ctx.measureText(text).width) / 2),
            textY = height / 2;
          ctx.fillText(text, textX, textY);
          ctx.save();
        }
      }
    },
  }

  const getGraphTitle = (type) => {
    switch (type) {
      case 'humidity':
        return 'Umidade ao Longo do Tempo';
      case 'ph':
        return data && data[data.length - 1]?.unidadePh &&
          data[data.length - 1].unidadePh.includes("Kg/m³") ? "Densidade ao Longo do Tempo" : 'PH ao Longo do Tempo';
      case 'temperature':
        return 'Temperatura ao Longo do Tempo';
      default:
        return '';
    }
  };

  // Verificar se há dados de pH válidos
  const hasValidPhData = useMemo(() => {
    if (!data || data.length === 0) return false;
    return data.some(item => item.ph && !isNaN(parseFloat(item.ph)));
  }, [data]);

  const phTitle =
    data && data[data.length - 1]?.unidadePh &&
    data[data.length - 1].unidadePh !== "NaN" &&
    data[data.length - 1].unidadePh.includes("Kg/m³")
      ? "Última Densidade"
      : "Último PH";

  const lastMeasurement = useMemo(() => {
    const lastItem = data?.[data.length - 1];
    if (!lastItem?.date) return null;

    return {
      time: dateFormat(lastItem.date, "UTC:HH:MM"),
      date: dateFormat(lastItem.date, "UTC:dd/mm/yy"),
    };
  }, [data]);

  const metricsColSize = hasValidPhData ? 3 : 4;

  const headerRight =
    data?.length > 0 ? `${data.length} registros` : null;

  const hasSelectionAnalysis = Boolean(selectionResult && selectedCount > 0);

  const showSeparateCharts = chartViewMode === "separate";
  const showCombinedHT = chartViewMode === "humidity-temperature";
  const showCombinedAll =
    hasValidPhData && chartViewMode === "humidity-temperature-density";

  const isDensityUnit = data?.[data.length - 1]?.unidadePh?.includes("Kg/m³");
  const combinedAllTitle = isDensityUnit
    ? "Umidade, Temperatura e Densidade"
    : "Umidade, Temperatura e pH";

  useEffect(() => {
    if (
      !hasValidPhData &&
      CHART_VIEW_MODES.find((mode) => mode.id === chartViewMode)?.requiresPh
    ) {
      handleChartViewModeChange("separate");
    }
  }, [hasValidPhData, chartViewMode, handleChartViewModeChange]);

  const renderChartLine = (options, chartData, isMobile, chartRef, mobileChartRef) => (
    <Line
      options={options}
      data={chartData}
      plugins={[plugins, ChartDataLabels]}
      height={isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT_DESKTOP}
      ref={isMobile ? mobileChartRef : chartRef}
    />
  );

  const renderChartsForViewport = (isMobile) => {
    const wrapClass = isMobile ? " dash-chart-wrap--mobile" : "";

    if (showSeparateCharts) {
      return (
        <>
          {humidity && (
            <DashMedidasSectionCard
              asPanel
              icon={FaTint}
              subtitle="Histórico"
              title={getGraphTitle("humidity")}
              bodyClassName={`dash-chart-wrap${wrapClass}`}
            >
              {renderChartLine(
                optionshumidity,
                humidity,
                isMobile,
                humidityChartRef,
                humidityChartRefMobile
              )}
            </DashMedidasSectionCard>
          )}

          {temperature && optionstemperature && (
            <DashMedidasSectionCard
              asPanel
              icon={FaThermometerHalf}
              subtitle="Histórico"
              title={getGraphTitle("temperature")}
              bodyClassName={`dash-chart-wrap${wrapClass}`}
            >
              {renderChartLine(
                optionstemperature,
                temperature,
                isMobile,
                temperatureChartRef,
                temperatureChartRefMobile
              )}
            </DashMedidasSectionCard>
          )}

          {ph &&
            ph.datasets &&
            ph.datasets[0] &&
            ph.datasets[0].data &&
            ph.datasets[0].data.filter((value) => value && !isNaN(value)).length > 0 && (
              <DashMedidasSectionCard
                asPanel
                icon={FaFlask}
                subtitle="Histórico"
                title={getGraphTitle("ph")}
                bodyClassName={`dash-chart-wrap${wrapClass}`}
              >
                {renderChartLine(optionsph, ph, isMobile, phChartRef, phChartRefMobile)}
              </DashMedidasSectionCard>
            )}
        </>
      );
    }

    return (
      <>
        {showCombinedHT && optionsCombinedHT && (
          <DashMedidasSectionCard
            asPanel
            icon={FaChartLine}
            subtitle="Comparativo"
            title="Umidade e Temperatura"
            bodyClassName={`dash-chart-wrap${wrapClass}`}
          >
            {renderChartLine(
              optionsCombinedHT,
              combinedHumidityTemperature,
              isMobile,
              combinedHTChartRef,
              combinedHTChartRefMobile
            )}
          </DashMedidasSectionCard>
        )}

        {showCombinedAll && optionsCombinedAll && (
          <DashMedidasSectionCard
            asPanel
            icon={FaChartLine}
            subtitle="Comparativo"
            title={combinedAllTitle}
            bodyClassName={`dash-chart-wrap${wrapClass}`}
          >
            {renderChartLine(
              optionsCombinedAll,
              combinedAllMetrics,
              isMobile,
              combinedAllChartRef,
              combinedAllChartRefMobile
            )}
          </DashMedidasSectionCard>
        )}
      </>
    );
  };

  return (
    <div className="dash-graficos-section w-100">
      <DashMedidasSectionCard
        icon={FaChartLine}
        subtitle="Monitoramento"
        title="Medições e Gráficos"
        headerRight={headerRight}
        collapsible
        sectionId="dash-graficos"
        mountOnEnter
        onExpand={handleChartsSectionExpand}
      >
        {data && data.length ? (
          <div className="dash-section-card__content-stack">
            <Row className="dash-medidas-grid">
              {lastMeasurement && (
                <Col xs={12} sm={12} md={metricsColSize} lg={metricsColSize}>
                  <DashMedidasSectionCard
                    compact
                    icon={FaClock}
                    title="Última medida às"
                    bodyClassName="text-center"
                    className="dash-metric-card--last-reading"
                  >
                    <h2 className="dash-metric-value dash-metric-value--datetime">
                      {lastMeasurement.date} {lastMeasurement.time}
                    </h2>
                  </DashMedidasSectionCard>
                </Col>
              )}

              <Col xs={12} sm={12} md={metricsColSize} lg={metricsColSize}>
                <DashMedidasSectionCard
                  compact
                  icon={FaTint}
                  title="Última Umidade"
                  bodyClassName="text-center"
                >
                  <h2 className="dash-metric-value">
                    {endValueHumidity
                      ? `${parseFloat(endValueHumidity).toFixed(1)}%`
                      : "--"}
                  </h2>
                </DashMedidasSectionCard>
              </Col>

              {hasValidPhData && (
                <Col xs={12} sm={12} md={metricsColSize} lg={metricsColSize}>
                  <DashMedidasSectionCard
                    compact
                    icon={FaThermometerHalf}
                    title="Última Temperatura"
                    bodyClassName="text-center"
                  >
                    <h2 className="dash-metric-value">
                      {endValueTemperature
                        ? `${parseFloat(endValueTemperature).toFixed(1)}°C`
                        : "--"}
                    </h2>
                  </DashMedidasSectionCard>
                </Col>
              )}

              {hasValidPhData ? (
                <Col xs={12} sm={12} md={metricsColSize} lg={metricsColSize}>
                  <DashMedidasSectionCard
                    compact
                    icon={FaFlask}
                    title={phTitle}
                    bodyClassName="text-center"
                  >
                    <h2 className="dash-metric-value">
                      {endValuePh && endValuePh !== "NaN" ? endValuePh : "--"}
                    </h2>
                  </DashMedidasSectionCard>
                </Col>
              ) : (
                <Col xs={12} sm={12} md={metricsColSize} lg={metricsColSize}>
                  <DashMedidasSectionCard
                    compact
                    icon={FaThermometerHalf}
                    title="Temperatura"
                    bodyClassName="text-center"
                  >
                    <h2 className="dash-metric-value">
                      {endValueTemperature
                        ? `${parseFloat(endValueTemperature).toFixed(1)}°C`
                        : "--"}
                    </h2>
                  </DashMedidasSectionCard>
                </Col>
              )}
            </Row>

            <div className="dash-chart-view-mode">
              <span className="dash-chart-view-mode__label">Visualização</span>
              <ToggleButtonGroup
                type="radio"
                name="dash-chart-view-mode"
                value={chartViewMode}
                onChange={handleChartViewModeChange}
                className="dash-btn-toggle-group dash-chart-view-mode__group"
              >
                {CHART_VIEW_MODES.map((mode) => (
                  <DashMedidasToggleButton
                    key={mode.id}
                    id={`chart-view-${mode.id}`}
                    value={mode.id}
                    variant="outline-success"
                    disabled={mode.requiresPh && !hasValidPhData}
                  >
                    {mode.label}
                  </DashMedidasToggleButton>
                ))}
              </ToggleButtonGroup>
            </div>

            <div
              className={`dash-chart-selection-bar${
                hasSelectionAnalysis ? " dash-chart-selection-bar--expanded" : ""
              }`}
            >
              <div className="dash-chart-selection-bar__header">
                <div className="dash-chart-selection-bar__info">
                  <span className="dash-chart-selection-bar__count">
                    {selectedCount} ponto{selectedCount === 1 ? "" : "s"} selecionado{selectedCount === 1 ? "" : "s"}
                  </span>
                  <span className="dash-chart-selection-bar__hint">
                    Arraste no gráfico para selecionar um lote. Clique em um ponto para alternar a seleção.
                    Pontos em losango têm dataset — ao clicar, escolha cálculos ou detalhes.
                  </span>
                </div>
                <div className="dash-chart-selection-bar__actions">
                  <DashMedidasButton
                    variant="outline-secondary"
                    disabled={selectedCount === 0}
                    onClick={clearSelection}
                  >
                    Limpar
                  </DashMedidasButton>
                </div>
              </div>

              <AnimatePresence initial={false}>
                {hasSelectionAnalysis && (
                  <motion.div
                    key="chart-selection-analysis"
                    className="dash-chart-selection-bar__analysis"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.52, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="dash-chart-selection-bar__analysis-header">
                      <BsCpu className="dash-chart-selection-bar__analysis-icon" aria-hidden />
                      <div className="dash-chart-selection-bar__analysis-titles">
                        <span className="dash-chart-selection-bar__analysis-subtitle">
                          Pontos selecionados
                        </span>
                        <span className="dash-chart-selection-bar__analysis-title">
                          Análise de {selectionResult.count} medição
                          {selectionResult.count === 1 ? "" : "ões"}
                        </span>
                      </div>
                    </div>
                    <div className="dash-chart-selection-bar__analysis-body">
                      {selectionResult.error ? (
                        <Alert variant="warning" className="mb-0">
                          {selectionResult.error}
                        </Alert>
                      ) : (
                        <GrainAnalysisCardsGrid analysis={selectionResult.analysis} />
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="dash-section-card__content-stack hidden-mobile">
              {renderChartsForViewport(false)}
            </div>

            <div className="dash-section-card__content-stack block-mobile">
              {renderChartsForViewport(true)}
            </div>
          </div>
        ) : (
          <div className="dash-section-empty">
            <p className="mb-0" style={{ fontSize: "0.85rem" }}>
              Nenhum dado disponível para exibir neste momento.
            </p>
          </div>
        )}
      </DashMedidasSectionCard>

      <MeasurePointActionModal
        show={Boolean(pointActionContext)}
        measure={pointActionContext?.measure}
        onHide={handlePointActionClose}
        onOpenCalculations={handlePointActionOpenCalculations}
        onOpenDatasetDetails={handlePointActionOpenDatasetDetails}
      />

      <MeasureMetadataModal
        show={Boolean(metadataModalMeasure)}
        measure={metadataModalMeasure}
        onHide={() => setMetadataModalMeasure(null)}
      />
    </div>
  );
}

export default Grafico;